import { normalize } from 'normalizr';

import { mapListToRecords, normalizeList, websocketsHandlerFactory } from '@sophocles/entities/entityUtils';
import { Relay, relaySchema } from '@sophocles/entities/Relay/relayRecord';
import { receiveRelayLoads, relayLoadSchema } from '@sophocles/entities/RelayLoad';
import { receiveRelaySchedules } from '@sophocles/entities/RelaySchedule';
import apiClient from '@sophocles/http-common';
import { checkHttpStatus, genericErrorAction } from '@sophocles/utils';

/* Receive Action */
export const RECEIVE_RELAYS = 'RECEIVE_RELAYS';
export function receiveRelays(json) {
  return {
    type: RECEIVE_RELAYS,
    relays: mapListToRecords(json, Relay),
  };
}

/* Sync Action — authoritative replace of all relays for a single controller.
 * Use when receiving a full relaycontroller payload whose nested relays array
 * is the new truth; upsert via receiveRelays alone leaves orphaned relays
 * from prior reconciliations. */
export const SYNC_RELAYS_FOR_CONTROLLER = 'SYNC_RELAYS_FOR_CONTROLLER';
export function syncRelaysForController(controllerId, relaysById) {
  return {
    type: SYNC_RELAYS_FOR_CONTROLLER,
    controllerId,
    relays: mapListToRecords(relaysById || {}, Relay),
  };
}

/* Request Actions */
export const REQUEST_SITE_GROUP_RELAYS = 'REQUEST_SITE_GROUP_RELAYS';

export const REQUEST_SITE_RELAYS = 'REQUEST_SITE_RELAYS';
function requestSiteRelays(site_id) {
  return {
    type: REQUEST_SITE_RELAYS,
    site_id,
  };
}

/* Fetch Actions */

export function fetchRelaysForSite(site_id) {
  return dispatch => {
    dispatch(requestSiteRelays(site_id));
    return apiClient
      .get(`/sites/${site_id}/relays/`)
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data.results, 'relays', relaySchema))
      .then(json => dispatch(receiveRelays(json.entities.relays)));
  };
}

/* Edit Actions */
export const EDIT_RELAY = 'EDIT_RELAY';
export function editRelay(relay) {
  return {
    type: EDIT_RELAY,
    relay,
  };
}

export function patchRelay(relay_id, values) {
  return dispatch => {
    dispatch(editRelay(relay_id));
    return apiClient
      .patch(`/relays/${relay_id}/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, relaySchema))
      .then(json => {
        dispatch(receiveRelays(json.entities.relays));
      })
      .catch(error => {
        dispatch(genericErrorAction(error, EDIT_RELAY));
      });
  };
}
/* Special Actions */

export const PAIR_RELAYS = 'PAIR_RELAYS';
export function pairRelays(relay_id, relay2_id) {
  return dispatch => {
    dispatch({
      type: PAIR_RELAYS,
      relay: relay_id,
      relay2: relay2_id,
    });
    const values = { id: relay2_id };
    return apiClient
      .post(`/relays/${relay_id}/pair/`, values)
      .then(checkHttpStatus)
      .then(response => ({
        relays: normalizeList(response.data.relays, 'relays', relaySchema),
        loads: normalizeList(response.data.loads, 'loads', relayLoadSchema),
      }))
      .then(json => {
        dispatch(receiveRelays(json.relays.entities.relays));
        return dispatch(receiveRelayLoads(json.loads.entities.loads));
      })
      .catch(error => {
        dispatch(genericErrorAction(error, EDIT_RELAY));
      });
  };
}

export const UNPAIR_RELAYS = 'UNPAIR_RELAYS';
export function unpairRelays(relay_id) {
  return dispatch => {
    dispatch({
      type: PAIR_RELAYS,
      relay: relay_id,
    });
    return apiClient
      .post(`/relays/${relay_id}/unpair/`, {})
      .then(checkHttpStatus)
      .then(response => normalizeList(response.data.relays, 'relays', relaySchema))
      .then(json => dispatch(receiveRelays(json.entities.relays)))
      .catch(error => {
        dispatch(genericErrorAction(error, EDIT_RELAY));
      });
  };
}

/* Create Actions */
export const CREATE_RELAY = 'CREATE_RELAY';
function sayCreateRelay(values) {
  return {
    type: CREATE_RELAY,
    values: values,
  };
}

export function createRelay(values) {
  return dispatch => {
    dispatch(sayCreateRelay(values));
    return apiClient
      .post(`/relays/`, values)
      .then(checkHttpStatus)
      .then(response => normalize(response.data, relaySchema))
      .then(json => dispatch(receiveRelays(json.entities.relays)))
      .catch(error => dispatch(genericErrorAction(error, CREATE_RELAY)));
  };
}

/* Delete Actions */
export const DELETE_RELAY = 'DELETE_RELAY';
export function sayDeleteRelay(relay) {
  return {
    type: DELETE_RELAY,
    relay,
  };
}

export function deleteRelay(relay_id) {
  return dispatch =>
    apiClient
      .delete(`/relays/${relay_id}/`)
      .then(checkHttpStatus)
      .then(r => dispatch(sayDeleteRelay(relay_id)))
      .catch(error => dispatch(genericErrorAction(error, DELETE_RELAY)));
}

const receiver = (dispatch, json) => {
  dispatch(receiveRelaySchedules(json.entities.relaySchedules));
  return dispatch(receiveRelays(json.entities.relays));
};

export const handleRelaySocketActions = websocketsHandlerFactory(receiver, sayDeleteRelay, relaySchema);
